import * as XLSX from 'xlsx';
import moment from 'moment';
import {
  BulkyDiseaseOptions,
  primaryDiagnosisOptions,
  primaryDiagnosisSubtypeOptions,
  PrimaryRefractoryOptions,
} from "../pages/appointment-details/common-pre-screen/common";
import {getLabelValue} from "./utils"
// Helper function to strip HTML tags from text
const stripHtmlTags = (text) => {
  if (!text) return '';
  return text.replace(/<[^>]*>/g, '').trim();
};

// Helper function to capitalize first letter
function capitalizeFirstLetter(str) {
  if (!str) return "N/A";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Helper function to format date
function formatDateMoment(date) {
  if (!date || !moment(date).isValid()) {
    return '-';
  }
  return moment(date).format('DD/MM/YYYY');
}

// Helper function to format date from YYYY-MM-DD
const formatDate = (dateString) => {
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Helper function to group data by patient
const groupDataByPatient = (data) => {
  const groupedData = {};

  data.forEach((item) => {
    const patientKey = `${item.subject.patient_id}_${item.subject.first_name}_${item.subject.last_name}`;

    if (!groupedData[patientKey]) {
      groupedData[patientKey] = {
        subject: item.subject,
        appointments: []
      };
    }

    groupedData[patientKey].appointments.push(...item.appointment);
  });

  return Object.values(groupedData);
};

// Helper function to determine the highest approval status for a visit
const getDataReviewStatus = (approvedUsers) => {
  if (!approvedUsers || approvedUsers.length === 0) {
    return 'Data Entered';
  }

  const hasCPM = approvedUsers.some(user =>
    user.includes('Clinical Phase Manager')
  );
  if (hasCPM) {
    return 'Data Approved by CPM';
  }

  const hasDQA = approvedUsers.some(user =>
    user.includes('Quality Associate')
  );
  if (hasDQA) {
    return 'Data Approved by DQA';
  }

  const hasPI = approvedUsers.some(user =>
    user.includes('Principal Investigator')
  );
  if (hasPI) {
    return 'Data Approved by PI';
  }

  return 'Data Entered';
};

// Helper function to extract role abbreviation from user string
const extractRoleAbbreviation = (userString) => {
  if (!userString) return userString;
  const lowerString = userString.toLowerCase();
  
  // Helper function to determine role type based on string content
  const getRoleType = (str) => {
    if (str.includes('principal investigator') || str.includes('pi')) return 'PI_ROLE';
    if (str.includes('quality associate') || str.includes('qa')) return 'QA_ROLE';
    if (str.includes('clinical phase manager') || str.includes('phase manager')) return 'PM_ROLE';
    if (str.includes('data manager')) return 'DM_ROLE';
    if (str.includes('monitor')) return 'MONITOR_ROLE';
    if (str.includes('superadmin') || str.includes('super admin')) return 'SUPERADMIN_ROLE';
    if (str.includes('admin')) return 'ADMIN_ROLE';
    return 'UNKNOWN_ROLE';
  };
  
  // Use switch-case for role abbreviation mapping
  switch (getRoleType(lowerString)) {
    case 'PI_ROLE':
      return 'PI';
    case 'QA_ROLE':
      return 'QA';
    case 'PM_ROLE':
      return 'Phase Manager';
    case 'DM_ROLE':
      return 'Data Manager';
    case 'MONITOR_ROLE':
      return 'Monitor';
    case 'SUPERADMIN_ROLE':
      return 'SuperAdmin';
    case 'ADMIN_ROLE':
      return 'Admin';
    default:
      return userString;
  }
};

const formatDateRangeSection = (data) => {
  const dateObject = typeof data == 'string' ? JSON.parse(data): data;
  if(dateObject?.start_date || dateObject?.stop_date){
    return `Start: ${dateObject.start_date}, Stop: ${dateObject.stop_date}`;
  }
  return ''
};
// Helper function to format queries by visit type with roles
const formatQueriesByVisitType = (queriesByVisitType) => {
  const formattedQueries = [];
  
  Object.keys(queriesByVisitType).forEach(visitType => {
    const users = queriesByVisitType[visitType];
    const roles = users.map(user => extractRoleAbbreviation(user));
    const uniqueRoles = [...new Set(roles)];
    
    if (uniqueRoles.length > 0) {
      formattedQueries.push(`${visitType}: ${uniqueRoles.join(', ')}`);
    }
  });
  
  return formattedQueries.join('; ');
};

// Helper function to format pending/approved users by visit type with roles
const formatUsersByVisitType = (usersByVisitType) => {
  const formattedUsers = [];
  
  Object.keys(usersByVisitType).forEach(visitType => {
    const users = usersByVisitType[visitType];
    const roles = users.map(user => extractRoleAbbreviation(user));
    const uniqueRoles = [...new Set(roles)];
    
    if (uniqueRoles.length > 0) {
      formattedUsers.push(`${visitType}: ${uniqueRoles.join(', ')}`);
    }
  });
  
  return formattedUsers.join('; ');
};

// Helper function to group appointments by visit type
const groupAppointmentsByVisitType = (appointments) => {
  const grouped = {};

  appointments.forEach((appointment) => {
    const visitType = appointment.form_title || 'Unknown';
    if (!grouped[visitType]) {
      grouped[visitType] = [];
    }
    grouped[visitType].push(appointment);
  });

  return grouped;
};

// Variable for Data Review Status dropdown options
const DATA_REVIEW_STATUS_OPTIONS = [
  'Data Entered',
  'Data Approved by PI',
  'Data Approved by DQA',
  'Data Approved by CPM'
];

// Helper function to get questions by visit type
const getQuestionsByVisitType = (data) => {
  const questionsByVisit = {};

  data.forEach((item) => {
    item.appointment.forEach((appointment) => {
      const visitType = appointment.form_title || 'Unknown';
      if (!questionsByVisit[visitType]) {
        questionsByVisit[visitType] = new Set();
      }

      appointment.reports.forEach((report) => {
        questionsByVisit[visitType].add(stripHtmlTags(report.question));
      });
    });
  });
  Object.keys(questionsByVisit).forEach(visitType => {
    questionsByVisit[visitType] = Array.from(questionsByVisit[visitType]);
  });

  return questionsByVisit;
};

const formatTreatmentHistory = (items) => {
    const treatment = typeof items == 'string' ? JSON.parse(items): items;

    const formattedTreatments = treatment.map(treatment => {
              const treatmentFor = treatment.treatment_for || 'N/A';
        const startDate = treatment.start_date || 'N/A';
        const endDate = treatment.end_date || 'N/A';
        
        return `Treatment for:${treatmentFor},Start:${startDate},Stop:${endDate}`;
    });
    
    // Join with pipeline separator if multiple records
    return formattedTreatments.join(' | ');
};
// Export to Excel with dropdowns
export const exportToExcelWithDropdowns = (data, filename) => {
  const groupedData = groupDataByPatient(data);

  const staticHeaders = [
    'S.No.',
    'Patient ID',
    'Patient Status',
    'Status Change'
  ];

  // Get all unique visit types
  const allVisitTypes = new Set();
  data.forEach((item) => {
    item.appointment.forEach((appointment) => {
      if (appointment.form_title) {
        allVisitTypes.add(appointment.form_title);
      }
    });
  });
  const visitTypesArray = Array.from(allVisitTypes);

  // Get questions organized by visit type
  const questionsByVisit = getQuestionsByVisitType(data);

  const dynamicHeaders = [];
  visitTypesArray.forEach(visitType => {
    const questions = questionsByVisit[visitType] || [];
    questions.forEach(question => {
      dynamicHeaders.push(question);
    });
    dynamicHeaders.push(`${visitType} - Data Review Status`);
  });

  const endingHeaders = [
    'Visit Types',
    'Appointment Titles',
    'Open Queries',
    'Pending Approval',
    'Approved',
  ];

  const allHeaders = [...staticHeaders, ...dynamicHeaders, ...endingHeaders];

  const rows = [];
  let serialNumber = 1;

  groupedData.forEach((patientData) => {
    const row = {};

    row['S.No.'] = serialNumber++;
    row['Patient ID'] = patientData.subject.patient_id || "-";
    row['Patient Status'] = capitalizeFirstLetter(patientData.subject.life_status);
    row['Status Change'] = formatDateMoment(patientData.subject.status_change_date);

    const appointmentsByVisit = groupAppointmentsByVisitType(patientData.appointments);

    const visitTypes = [];
    const appointmentTitles = [];
    
    // Objects to group data by visit type for better organization
    const queriesByVisitType = {};
    const pendingUsersByVisitType = {};
    const approvedUsersByVisitType = {};

    patientData.appointments.forEach((appointment) => {
      const visitType = appointment.form_title || 'Unknown Visit';
      
      if (appointment.form_title) visitTypes.push(appointment.form_title);
      if (appointment.title) appointmentTitles.push(appointment.title);

      // Group queries by visit type
      if (appointment.query_raised?.length > 0) {
        if (!queriesByVisitType[visitType]) {
          queriesByVisitType[visitType] = [];
        }
        queriesByVisitType[visitType].push(...appointment.query_raised);
      }

      // Group pending users by visit type
      if (appointment.pending_users?.length > 0) {
        if (!pendingUsersByVisitType[visitType]) {
          pendingUsersByVisitType[visitType] = [];
        }
        pendingUsersByVisitType[visitType].push(...appointment.pending_users);
      }

      // Group approved users by visit type
      if (appointment.approved_users?.length > 0) {
        if (!approvedUsersByVisitType[visitType]) {
          approvedUsersByVisitType[visitType] = [];
        }
        approvedUsersByVisitType[visitType].push(...appointment.approved_users);
      }
    });

    // Remove duplicates from each visit type group
    Object.keys(queriesByVisitType).forEach(visitType => {
      queriesByVisitType[visitType] = [...new Set(queriesByVisitType[visitType])];
    });
    
    Object.keys(pendingUsersByVisitType).forEach(visitType => {
      pendingUsersByVisitType[visitType] = [...new Set(pendingUsersByVisitType[visitType])];
    });
    
    Object.keys(approvedUsersByVisitType).forEach(visitType => {
      approvedUsersByVisitType[visitType] = [...new Set(approvedUsersByVisitType[visitType])];
    });

    visitTypesArray.forEach(visitType => {
      const questions = questionsByVisit[visitType] || [];
      const visitsOfType = appointmentsByVisit[visitType] || [];

      // Collect answers for this visit type
      const answersForVisit = {};
      visitsOfType.forEach(appointment => {
        appointment.reports.forEach((report) => {
         
          const question = stripHtmlTags(report.question);
          let answer = report?.answer;

              let options = [];
                 if (question === 'Bulky disease (DLBCL)') {
                   options = BulkyDiseaseOptions;
                 } else if (question == "Biopsy Confirmed PR" || question === 'Primary Refractory PR' || question === "Cytogenetic Report Available PR") {
                   options = PrimaryRefractoryOptions;
                 } else if (question === 'Primary Diagnosis PR') {
                   options = primaryDiagnosisOptions;
                 } else if (question === 'Subtype PR') {
                   options = primaryDiagnosisSubtypeOptions;
                 }
                if(options && options?.length > 0){
                 const label =  getLabelValue(options, answer);
                  answer = label;
                }else if (question === 'Date of Birth PR' || (typeof answer === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(answer))) {
                  answer = formatDateMoment(answer);
             }

          if (Array.isArray(answer)) {
            answer = answer.filter(Boolean).join(', ');
          } else if (answer == null || answer == "") {
            answer = '';
          } else {
            answer = String(answer).replace(/\n/g, ' ');
          }
          if (answer && answer.trim() != '') {
            if (!answersForVisit[question]) {
              answersForVisit[question] = [];
            }
            answersForVisit[question].push(answer);
          }
        });
      });
  
      // Add answers for each question of this visit type
      questions.forEach(question => {
        const answers = answersForVisit[question] || [];
         const uniqueAnswers = [...new Set(answers)];
        if(question == 'Last Regimen Start/Stop Dates PR'){
           row[question] = answers?.length > 0 ? formatDateRangeSection(answers?.[0]):''
        }else if(question == 'Treatment History PR'){
           row[question] = answers?.length > 0 ? formatTreatmentHistory(answers?.[0]):''
        }else{
        row[question] = uniqueAnswers.length > 0 ? uniqueAnswers.join(' | ') : '';
        }
      });
      // Add Data Review Status for this visit type
      const allApprovedForVisit = [];
      visitsOfType.forEach(visit => {
        if (visit.approved_users?.length > 0) {
          allApprovedForVisit.push(...visit.approved_users);
        }
      });
      const status = getDataReviewStatus(allApprovedForVisit);
      row[`${visitType} - Data Review Status`] = status;
    });
    // Format the final columns using the new formatting functions
    row['Visit Types'] = visitTypes.length > 0 ? [...new Set(visitTypes)].join(' , ') : 'N/A';
    row['Appointment Titles'] = appointmentTitles.length > 0 ? [...new Set(appointmentTitles)].join(' , ') : 'N/A';
    row['Open Queries'] = Object.keys(queriesByVisitType).length > 0 ? formatQueriesByVisitType(queriesByVisitType) : 'N/A';
    row['Pending Approval'] = Object.keys(pendingUsersByVisitType).length > 0 ? formatUsersByVisitType(pendingUsersByVisitType) : 'N/A';
    row['Approved'] = Object.keys(approvedUsersByVisitType).length > 0 ? formatUsersByVisitType(approvedUsersByVisitType) : 'N/A';

    rows.push(row);
  });

  const worksheet = XLSX.utils.json_to_sheet(rows, { header: allHeaders });

  const range = XLSX.utils.decode_range(worksheet['!ref']);

  const statusColumnIndices = [];
  allHeaders.forEach((header, index) => {
    if (header.includes('- Data Review Status')) {
      statusColumnIndices.push(index);
    }
  });

  // Add data validation for each status column
  if (!worksheet['!dataValidation']) {
    worksheet['!dataValidation'] = [];
  }

  statusColumnIndices.forEach(colIndex => {
    for (let rowIndex = 1; rowIndex <= range.e.r; rowIndex++) {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });

      worksheet['!dataValidation'].push({
        sqref: cellAddress,
        type: 'list',
        formula1: `"${DATA_REVIEW_STATUS_OPTIONS.join(',')}"`,
        showDropDown: true,
        allowBlank: false
      });
    }
  });

  // Set column widths
  const colWidths = allHeaders.map(header => {
    if (header.includes('- Data Review Status')) {
      return { wch: 25 };
    } else if (header.length > 20) {
      return { wch: 30 };
    } else {
      return { wch: 15 };
    }
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Patient Data');

  XLSX.writeFile(workbook, filename);
};

export const exportToExcel = (data, filename) => {
  const groupedData = groupDataByPatient(data);

  const staticHeaders = [
    'S.No.',
    'Patient ID',
    'Patient Status',
    'Status Change'
  ];

  // Get all unique visit types
  const allVisitTypes = new Set();
  data.forEach((item) => {
    item.appointment.forEach((appointment) => {
      if (appointment.form_title) {
        allVisitTypes.add(appointment.form_title);
      }
    });
  });
  const visitTypesArray = Array.from(allVisitTypes);

  // Get questions organized by visit type
  const questionsByVisit = getQuestionsByVisitType(data);

  const dynamicHeaders = [];
  visitTypesArray.forEach(visitType => {
    const questions = questionsByVisit[visitType] || [];
    questions.forEach(question => {
      dynamicHeaders.push(question);
    });
    dynamicHeaders.push(`${visitType} - Data Review Status`);
  });

  const endingHeaders = [
    'Visit Types',
    'Appointment Titles',
    'Open Queries',
    'Pending Approval',
    'Approved',
  ];

  const allHeaders = [...staticHeaders, ...dynamicHeaders, ...endingHeaders];

  const rows = [];
  let serialNumber = 1;

  groupedData.forEach((patientData) => {
    const row = {};

    row['S.No.'] = serialNumber++;
    row['Patient ID'] = patientData.subject.patient_id || "-";
    row['Patient Status'] = capitalizeFirstLetter(patientData.subject.life_status);
    row['Status Change'] = formatDateMoment(patientData.subject.status_change_date);

    const appointmentsByVisit = groupAppointmentsByVisitType(patientData.appointments);

    const visitTypes = [];
    const appointmentTitles = [];
    
    // Objects to group data by visit type for better organization
    const queriesByVisitType = {};
    const pendingUsersByVisitType = {};
    const approvedUsersByVisitType = {};

    patientData.appointments.forEach((appointment) => {
      const visitType = appointment.form_title || 'Unknown Visit';
      
      if (appointment.form_title) visitTypes.push(appointment.form_title);
      if (appointment.title) appointmentTitles.push(appointment.title);

      // Group queries by visit type
      if (appointment.query_raised?.length > 0) {
        if (!queriesByVisitType[visitType]) {
          queriesByVisitType[visitType] = [];
        }
        queriesByVisitType[visitType].push(...appointment.query_raised);
      }

      // Group pending users by visit type
      if (appointment.pending_users?.length > 0) {
        if (!pendingUsersByVisitType[visitType]) {
          pendingUsersByVisitType[visitType] = [];
        }
        pendingUsersByVisitType[visitType].push(...appointment.pending_users);
      }

      // Group approved users by visit type
      if (appointment.approved_users?.length > 0) {
        if (!approvedUsersByVisitType[visitType]) {
          approvedUsersByVisitType[visitType] = [];
        }
        approvedUsersByVisitType[visitType].push(...appointment.approved_users);
      }
    });

    // Remove duplicates from each visit type group
    Object.keys(queriesByVisitType).forEach(visitType => {
      queriesByVisitType[visitType] = [...new Set(queriesByVisitType[visitType])];
    });
    
    Object.keys(pendingUsersByVisitType).forEach(visitType => {
      pendingUsersByVisitType[visitType] = [...new Set(pendingUsersByVisitType[visitType])];
    });
    
    Object.keys(approvedUsersByVisitType).forEach(visitType => {
      approvedUsersByVisitType[visitType] = [...new Set(approvedUsersByVisitType[visitType])];
    });

    visitTypesArray.forEach(visitType => {
      const questions = questionsByVisit[visitType] || [];
      const visitsOfType = appointmentsByVisit[visitType] || [];

      const answersForVisit = {};
      visitsOfType.forEach(appointment => {
        appointment.reports.forEach((report) => {
          const question = stripHtmlTags(report.question);
          let answer = report?.answer || '';

          if (typeof answer === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(answer)) {
            answer = formatDate(answer);
          }

          if (Array.isArray(answer)) {
            answer = answer.filter(Boolean).join(', ');
          } else if (answer === null || answer === "") {
            answer = '';
          } else {
            answer = String(answer).replace(/\n/g, ' ');
          }

          if (answer && answer.trim() !== '') {
            if (!answersForVisit[question]) {
              answersForVisit[question] = [];
            }
            answersForVisit[question].push(answer);
          }
        });
      });

      // Add answers for each question of this visit type
      questions.forEach(question => {
        const answers = answersForVisit[question] || [];
        const uniqueAnswers = [...new Set(answers)];
        row[question] = uniqueAnswers.length > 0 ? uniqueAnswers.join(' | ') : '';
      });

      // Add Data Review Status for this visit type
      const allApprovedForVisit = [];
      visitsOfType.forEach(visit => {
        if (visit.approved_users?.length > 0) {
          allApprovedForVisit.push(...visit.approved_users);
        }
      });
      const status = getDataReviewStatus(allApprovedForVisit);
      row[`${visitType} - Data Review Status`] = status;
    });

    // Format the final columns using the new formatting functions
    row['Visit Types'] = visitTypes.length > 0 ? [...new Set(visitTypes)].join(' | ') : 'N/A';
    row['Appointment Titles'] = appointmentTitles.length > 0 ? [...new Set(appointmentTitles)].join(' | ') : 'N/A';
    row['Open Queries'] = Object.keys(queriesByVisitType).length > 0 ? formatQueriesByVisitType(queriesByVisitType) : 'N/A';
    row['Pending Approval'] = Object.keys(pendingUsersByVisitType).length > 0 ? formatUsersByVisitType(pendingUsersByVisitType) : 'N/A';
    row['Approved'] = Object.keys(approvedUsersByVisitType).length > 0 ? formatUsersByVisitType(approvedUsersByVisitType) : 'N/A';

    rows.push(row);
  });
  const worksheet = XLSX.utils.json_to_sheet(rows, { header: allHeaders });

  const colWidths = allHeaders.map(header => {
    if (header.includes('- Data Review Status')) {
      return { wch: 25 };
    } else if (header.length > 20) {
      return { wch: 30 };
    } else {
      return { wch: 15 };
    }
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Patient Data');

  XLSX.writeFile(workbook, filename);
};